/**
 * GTM DataLayer Events - Global Helper
 * Reusable methods for consistent event tracking across the storefront
 */
window.GTMEvents = (function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];


  function push(data) {
    // Clear ecommerce object before each push (GA4 best practice)
    if (data.ecommerce) {
      window.dataLayer.push({ ecommerce: null });
    }
    window.dataLayer.push(data);
  }

  return {
    // ─── GA4 RECOMMENDED EVENTS ───────────────────────────────────

    /**
     * View Item - Standard GA4 Ecommerce Event
     * @param {Object} product - {id, name, price, brand, category, variant, currency}
     */
    viewItem: function (product) {
      push({
        event: 'view_item',
        ecommerce: {
          currency: product.currency || 'GBP',
          value: parseFloat(product.price) || 0,
          items: [{
            item_id: String(product.id),
            item_name: product.name,
            item_brand: product.brand || '',
            item_category: product.category || '',
            item_variant: product.variant || '',
            price: parseFloat(product.price) || 0,
            quantity: 1
          }]
        }
      });
    },

    /**
     * View Item List - Standard GA4 Ecommerce Event
     * @param {Array} items - [{id, name, price, brand, category, position}]
     * @param {String} listName - Name of the list (e.g., "Search Results", "Category")
     * @param {String} currency - Currency code
     */
    viewItemList: function (items, listName, currency) {
      push({
        event: 'view_item_list',
        ecommerce: {
          item_list_name: listName || 'Product List',
          currency: currency || 'GBP',
          items: items.map(function (i, index) {
            return {
              item_id: String(i.id),
              item_name: i.name,
              item_brand: i.brand || '',
              item_category: i.category || '',
              price: parseFloat(i.price) || 0,
              index: i.position || index
            };
          })
        }
      });
    },

    /**
     * Add to Cart - Standard GA4 Ecommerce Event
     * @param {Array} items - [{id, name, price, quantity, variant}]
     * @param {String} currency - Currency code
     */
    addToCart: function (items, currency) {
      var value = items.reduce(function (sum, i) {
        return sum + (parseFloat(i.price) * parseInt(i.quantity));
      }, 0);
      
      push({
        event: 'add_to_cart',
        ecommerce: {
          currency: currency || 'GBP',
          value: value,
          items: items.map(function (i) {
            return {
              item_id: String(i.id),
              item_name: i.name,
              item_variant: i.variant || '',
              price: parseFloat(i.price),
              quantity: parseInt(i.quantity)
            };
          })
        }
      });
    },

    /**
     * Remove from Cart - Standard GA4 Ecommerce Event
     * @param {Array} items - [{id, name, price, quantity, variant}]
     * @param {String} currency - Currency code
     */
    removeFromCart: function (items, currency) {
      var value = items.reduce(function (sum, i) {
        return sum + (parseFloat(i.price) * parseInt(i.quantity));
      }, 0);
      
      push({
        event: 'remove_from_cart',
        ecommerce: {
          currency: currency || 'GBP',
          value: value,
          items: items.map(function (i) {
            return {
              item_id: String(i.id),
              item_name: i.name,
              item_variant: i.variant || '',
              price: parseFloat(i.price),
              quantity: parseInt(i.quantity)
            };
          })
        }
      });
    },

    /**
     * Add to Wishlist - Standard GA4 Ecommerce Event
     * @param {Object} product - {id, name, price, currency}
     */
    addToWishlist: function (product) {
      push({
        event: 'add_to_wishlist',
        ecommerce: {
          currency: product.currency || 'GBP',
          value: parseFloat(product.price) || 0,
          items: [{
            item_id: String(product.id),
            item_name: product.name,
            price: parseFloat(product.price) || 0,
            quantity: 1
          }]
        }
      });
    },

    /**
     * Login - Standard GA4 User Event
     * @param {String} userId
     * @param {String} method - 'email', 'google', etc.
     */
    login: function (userId, method) {
      if (sessionStorage.getItem('gtm_login_tracked')) return;
      push({
        event: 'login',
        method: method || 'email',
        user_id: String(userId)
      });
      sessionStorage.setItem('gtm_login_tracked', 'true');
    },

    /**
     * Sign Up - Standard GA4 User Event
     * @param {String} userId
     * @param {String} method - 'email', 'google', etc.
     */
    signUp: function (userId, method) {
      if (sessionStorage.getItem('gtm_signup_tracked')) return;
      push({
        event: 'sign_up',
        method: method || 'email',
        user_id: String(userId)
      });
      sessionStorage.setItem('gtm_signup_tracked', 'true');
    },

    // ─── CUSTOM EVENTS ────────────────────────────────────────────

    /**
     * Contact Us - Custom Lead Generation Event
     * @param {String} formId - Form identifier
     * @param {String} method - 'contact_form', 'email', 'phone'
     */
    contactUs: function (formId, method) {
      push({
        event: 'contact_us',
        form_id: formId || 'contact_form',
        method: method || 'contact_form'
      });
    },

    /**
     * Form Start - Custom Engagement Event
     * @param {String} formId - Form identifier
     */
    formStart: function (formId) {
      push({
        event: 'form_start',
        form_id: formId || 'unknown'
      });
    },

    /**
     * Chat Served by Operator - Custom Support Event
     * @param {String} operator - Operator name/ID
     */
    chatServed: function (operator) {
      push({
        event: 'chat_served_by_operator',
        operator_id: operator || ''
      });
    },

    /**
     * Chatbot Interaction - Custom Bot Event
     * @param {String} action - Interaction type
     * @param {String} label - Additional context
     */
    chatbotInteraction: function (action, label) {
      push({
        event: 'chatbot_interaction',
        action: action || '',
        label: label || ''
      });
    },

    /**
     * Sign-up Credit - Custom Promotion Event
     * @param {String} userId
     * @param {Number} creditAmount
     */
    signUpCredit: function (userId, creditAmount) {
      push({
        event: 'sign_up_credit',
        user_id: String(userId),
        credit_amount: parseFloat(creditAmount) || 0
      });
    },

    // ─── GENERIC PUSH ─────────────────────────────────────────────

    /**
     * Custom Event Push
     * @param {String} eventName
     * @param {Object} data - Additional parameters
     */
    custom: function (eventName, data) {
      data = data || {};
      data.event = eventName;
      push(data);
    }
  };
})();

// ─── AUTO: Form Start Listener ────────────────────────────────────
document.addEventListener('focusin', function (e) {
  var form = e.target.closest('form');
  if (form && !form.dataset.gtmFormStarted) {
    form.dataset.gtmFormStarted = 'true';
    window.GTMEvents.formStart(form.id || form.getAttribute('action') || 'unknown');
  }
});

// ─── AUTO: Scroll Tracking ────────────────────────────────────────
(function () {
  var scrollDepths = [25, 50, 75, 100];
  var trackedDepths = {};

  function getScrollPercentage() {
    var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    var docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    return docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
  }

  function trackScrollDepth() {
    var currentDepth = getScrollPercentage();
    
    scrollDepths.forEach(function (depth) {
      if (currentDepth >= depth && !trackedDepths[depth]) {
        trackedDepths[depth] = true;
        
        window.dataLayer.push({
          event: 'scrollTracking',
          eventCategory: 'Scroll Tracking',
          eventAction: 'Scroll Depth',
          eventLabel: depth + '%',
          eventValue: depth,
          nonInteraction: true
        });
      }
    });
  }

  var scrollTimeout;
  window.addEventListener('scroll', function () {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(trackScrollDepth, 100);
  }, { passive: true });

  if (document.readyState === 'complete') {
    trackScrollDepth();
  } else {
    window.addEventListener('load', trackScrollDepth);
  }
})();
